import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, AlertTriangle, Zap, Search, Globe, Image, FileText, Clock } from 'lucide-react';

interface SEOCheck {
  id: string;
  name: string;
  category: 'meta' | 'performance' | 'structure' | 'content' | 'technical';
  status: 'pass' | 'warning' | 'fail' | 'pending';
  message: string;
  impact: 'high' | 'medium' | 'low';
  recommendation?: string;
}

interface SEOValidationResult {
  score: number;
  checks: SEOCheck[];
  summary: {
    passed: number;
    warnings: number;
    failed: number;
    total: number;
  };
}

const SEOValidator: React.FC = () => {
  const [isValidating, setIsValidating] = useState(false);
  const [result, setResult] = useState<SEOValidationResult | null>(null);

  const validateSEO = async (): Promise<SEOValidationResult> => {
    const checks: SEOCheck[] = [];

    // Meta标签检查
    const title = document.title;
    const description = document.querySelector('meta[name="description"]')?.getAttribute('content');
    const keywords = document.querySelector('meta[name="keywords"]')?.getAttribute('content');
    const canonical = document.querySelector('link[rel="canonical"]')?.getAttribute('href');
    
    checks.push({
      id: 'title-length',
      name: '标题长度',
      category: 'meta',
      status: title && title.length > 10 && title.length < 60 ? 'pass' : 'warning',
      message: `标题长度: ${title?.length || 0} 字符`,
      impact: 'high',
      recommendation: '建议标题长度在10-60字符之间'
    });

    checks.push({
      id: 'description-present',
      name: '描述标签',
      category: 'meta',
      status: description && description.length > 50 && description.length < 160 ? 'pass' : 'warning',
      message: description ? `描述长度: ${description.length} 字符` : '缺少描述标签',
      impact: 'high',
      recommendation: '建议描述长度在50-160字符之间'
    });

    checks.push({
      id: 'keywords-present',
      name: '关键词标签',
      category: 'meta',
      status: keywords ? 'pass' : 'warning',
      message: keywords ? '关键词标签存在' : '缺少关键词标签',
      impact: 'medium',
      recommendation: '添加相关关键词有助于SEO'
    });

    checks.push({
      id: 'canonical-url',
      name: '规范化URL',
      category: 'technical',
      status: canonical ? 'pass' : 'warning',
      message: canonical ? '规范化URL已设置' : '缺少规范化URL',
      impact: 'medium',
      recommendation: '设置规范化URL避免重复内容'
    });

    // Open Graph检查
    const ogTitle = document.querySelector('meta[property="og:title"]')?.getAttribute('content');
    const ogDescription = document.querySelector('meta[property="og:description"]')?.getAttribute('content');
    const ogImage = document.querySelector('meta[property="og:image"]')?.getAttribute('content');

    checks.push({
      id: 'og-tags',
      name: 'Open Graph标签',
      category: 'meta',
      status: ogTitle && ogDescription && ogImage ? 'pass' : 'warning',
      message: `OG标签完整度: ${[ogTitle, ogDescription, ogImage].filter(Boolean).length}/3`,
      impact: 'medium',
      recommendation: '完整的OG标签有助于社交媒体分享'
    });

    // 结构化数据检查
    const structuredData = document.querySelector('script[type="application/ld+json"]');
    checks.push({
      id: 'structured-data',
      name: '结构化数据',
      category: 'structure',
      status: structuredData ? 'pass' : 'warning',
      message: structuredData ? '结构化数据存在' : '缺少结构化数据',
      impact: 'high',
      recommendation: '添加Schema.org结构化数据提升搜索引擎理解'
    });

    // 图像alt标签检查
    const images = document.querySelectorAll('img');
    const imagesWithoutAlt = Array.from(images).filter(img => !img.alt);
    checks.push({
      id: 'images-alt',
      name: '图片Alt标签',
      category: 'content',
      status: imagesWithoutAlt.length === 0 ? 'pass' : 'warning',
      message: `${images.length - imagesWithoutAlt.length}/${images.length} 图片有Alt标签`,
      impact: 'medium',
      recommendation: '为所有图片添加描述性Alt标签'
    });

    // 标题结构检查
    const h1Tags = document.querySelectorAll('h1');
    checks.push({
      id: 'h1-structure',
      name: 'H1标签结构',
      category: 'structure',
      status: h1Tags.length === 1 ? 'pass' : h1Tags.length === 0 ? 'fail' : 'warning',
      message: `页面包含 ${h1Tags.length} 个H1标签`,
      impact: 'high',
      recommendation: '每个页面应该有且仅有一个H1标签'
    });

    // 响应式设计检查
    const viewport = document.querySelector('meta[name="viewport"]')?.getAttribute('content');
    checks.push({
      id: 'mobile-friendly',
      name: '移动端适配',
      category: 'technical',
      status: viewport?.includes('width=device-width') ? 'pass' : 'fail',
      message: viewport ? '视窗标签存在' : '缺少视窗标签',
      impact: 'high',
      recommendation: '设置正确的视窗标签确保移动端体验'
    });

    // 页面加载速度检查
    const pageLoadTime = performance.timing.loadEventEnd - performance.timing.navigationStart;
    checks.push({
      id: 'page-speed',
      name: '页面加载速度',
      category: 'performance',
      status: pageLoadTime < 3000 ? 'pass' : pageLoadTime < 5000 ? 'warning' : 'fail',
      message: `页面加载时间: ${(pageLoadTime / 1000).toFixed(2)}秒`,
      impact: 'high',
      recommendation: '优化页面加载速度，建议控制在3秒内'
    });

    // HTTPS检查
    checks.push({
      id: 'https-secure',
      name: 'HTTPS安全',
      category: 'technical',
      status: window.location.protocol === 'https:' ? 'pass' : 'fail',
      message: window.location.protocol === 'https:' ? '使用HTTPS协议' : '未使用HTTPS协议',
      impact: 'high',
      recommendation: '使用HTTPS确保网站安全性'
    });

    // 内部链接检查
    const internalLinks = document.querySelectorAll('a[href^="/"], a[href^="#"]');
    checks.push({
      id: 'internal-links',
      name: '内部链接',
      category: 'structure',
      status: internalLinks.length > 0 ? 'pass' : 'warning',
      message: `页面包含 ${internalLinks.length} 个内部链接`,
      impact: 'medium',
      recommendation: '适当的内部链接有助于网站导航和SEO'
    });

    // 计算总体评分
    const summary = {
      passed: checks.filter(c => c.status === 'pass').length,
      warnings: checks.filter(c => c.status === 'warning').length,
      failed: checks.filter(c => c.status === 'fail').length,
      total: checks.length
    };

    const score = Math.round((summary.passed / summary.total) * 100);

    return { score, checks, summary };
  };

  const handleValidate = async () => {
    setIsValidating(true);
    try {
      // 添加延迟以显示加载状态
      await new Promise(resolve => setTimeout(resolve, 1000));
      const result = await validateSEO();
      setResult(result);
    } catch (error) {
      console.error('SEO validation error:', error);
    } finally {
      setIsValidating(false);
    }
  };

  const getStatusIcon = (status: SEOCheck['status']) => {
    switch (status) {
      case 'pass':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'fail':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: SEOCheck['status']) => {
    switch (status) {
      case 'pass':
        return 'bg-green-100 text-green-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'fail':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category: SEOCheck['category']) => {
    switch (category) {
      case 'meta':
        return <FileText className="w-4 h-4" />;
      case 'performance':
        return <Zap className="w-4 h-4" />;
      case 'structure':
        return <Globe className="w-4 h-4" />;
      case 'content':
        return <Image className="w-4 h-4" />;
      case 'technical':
        return <Search className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  useEffect(() => {
    // 页面加载时自动验证
    handleValidate();
  }, []);

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="w-5 h-5" />
          SEO技术验证
        </CardTitle>
        <CardDescription>
          检查网站的SEO技术配置和性能指标
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* 验证按钮和总体评分 */}
        <div className="flex items-center justify-between">
          <Button 
            onClick={handleValidate} 
            disabled={isValidating}
            className="flex items-center gap-2"
          >
            <Search className="w-4 h-4" />
            {isValidating ? '验证中...' : '重新验证'}
          </Button>
          
          {result && (
            <div className="text-right">
              <div className={`text-2xl font-bold ${getScoreColor(result.score)}`}>
                {result.score}/100
              </div>
              <div className="text-sm text-gray-600">SEO评分</div>
            </div>
          )}
        </div>

        {/* 加载进度 */}
        {isValidating && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">正在分析SEO配置...</span>
            </div>
            <Progress value={85} className="w-full" />
          </div>
        )}

        {/* 验证结果概览 */}
        {result && (
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{result.summary.passed}</div>
              <div className="text-sm text-gray-600">通过</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{result.summary.warnings}</div>
              <div className="text-sm text-gray-600">警告</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{result.summary.failed}</div>
              <div className="text-sm text-gray-600">失败</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{result.summary.total}</div>
              <div className="text-sm text-gray-600">总计</div>
            </div>
          </div>
        )}

        {/* 详细检查结果 */}
        {result && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">详细检查结果</h3>
            {result.checks.map((check) => (
              <div key={check.id} className="p-4 border rounded-lg hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getCategoryIcon(check.category)}
                    <span className="font-medium">{check.name}</span>
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${getStatusColor(check.status)}`}
                    >
                      {check.status === 'pass' ? '通过' : 
                       check.status === 'warning' ? '警告' : '失败'}
                    </Badge>
                  </div>
                  {getStatusIcon(check.status)}
                </div>
                
                <div className="mb-2 text-sm text-gray-600">
                  {check.message}
                </div>
                
                {check.recommendation && check.status !== 'pass' && (
                  <Alert className="mt-2">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-sm">
                      <strong>建议：</strong>{check.recommendation}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            ))}
          </div>
        )}

        {/* SEO优化建议 */}
        {result && result.score < 80 && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>SEO优化建议：</strong>
              您的网站SEO评分为 {result.score}/100，建议优先处理失败和警告项目以提升搜索引擎排名。
              特别关注标题、描述、结构化数据和页面性能等关键因素。
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default SEOValidator;